// Helpers for safely round-tripping a "where the user was going" path through
// the auth flow. Anything we navigate to from a search-param or OIDC state must
// be validated to avoid open-redirects (e.g. //evil.com, https://evil.com) and
// to avoid bouncing back into the auth pages themselves.

import * as R from 'remeda';

// Routes we never want to land on after a successful login — they would either
// loop the user back through auth or strand them on a transient page.
const FORBIDDEN_TARGETS = new Set(['/login', '/signin-oidc']);

/**
 * Returns the input only when it is a same-origin internal path that is safe
 * to navigate to after auth. Returns `undefined` for anything else (including
 * absolute URLs, protocol-relative paths, and the auth routes themselves).
 *
 * Callers fall back to `START_PAGE` when this returns `undefined`.
 */
export const safeRedirect = (
  value: string | null | undefined,
): string | undefined => {
  if (!R.isDefined(value)) return undefined;
  // Must start with a single slash — rejects "//evil.com" and "https://..."
  if (!value.startsWith('/')) return undefined;
  if (value.startsWith('//')) return undefined;

  // Strip search/hash before comparing against the forbidden list, so
  // /login?foo=bar still loops back the same as bare /login.
  const pathOnly = value.replace(/[?#].*$/, '');
  if (FORBIDDEN_TARGETS.has(pathOnly)) return undefined;

  return value;
};
